import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@material-ui/core';
import { Trans } from '@lingui/macro';
import NgpRow from '../../../form/NgpRow';
import { deepMergeForSave } from './TestDetailMergeUtils';
import { useTestValidation } from './hooks/UseTestValidation';

/**
 * Bottom action buttons (Save + Verify) for the test detail form.
 * Replicates the same validation and submit logic as the toolbar buttons
 * in TestDetailActionButtons, ensuring consistent behavior.
 *
 * @param {Object} props
 * @param {Object} props.formData - Current form data for validation
 * @param {Function} props.handleSubmit - react-hook-form handleSubmit
 * @param {Function} props.handleSubmitFunction - Save submit function from InfrastructureDetailBase
 * @param {boolean} props.saveDisabled - Whether save is disabled (saving/actioning)
 * @param {boolean} props.isActioning - Whether a data table action is in progress
 * @param {Function} props.onVerify - Verify callback from TestDetailPanel
 * @param {boolean} props.verifyLoading - Whether verify is in progress
 */
// Intentional parallel with TestDetailActionButtons — both maintain independent
// useTestValidation calls and handler definitions. See Phase 8 D-03.
const TestDetailBottomButtons = ({
    groupExist,
    formData,
    handleSubmit,
    handleSubmitFunction,
    submitTransform,
    saveDisabled,
    isActioning,
    onVerify,
    verifyLoading,
    bttStateRef,
}) => {
    const validateTest = useTestValidation(groupExist, formData, bttStateRef);

    const handleSaveAction = useCallback(
        (event) => {
            event.currentTarget.blur();
            event.preventDefault();
            event.stopPropagation();
            handleSubmit(validateTest(handleSubmitFunction))();
        },
        [handleSubmit, validateTest, handleSubmitFunction]
    );

    const handleVerifyAction = useCallback(
        (event) => {
            event.currentTarget.blur();
            event.preventDefault();
            event.stopPropagation();
            if (onVerify) {
                handleSubmit(
                    validateTest((validatedData) => {
                        const merged = deepMergeForSave(formData, validatedData);
                        const afterSubmitTransform = submitTransform ? submitTransform(merged) : merged;
                        return onVerify(afterSubmitTransform);
                    })
                )();
            }
        },
        [handleSubmit, validateTest, onVerify, formData, submitTransform]
    );

    return (
        <NgpRow>
            <Button
                onClick={handleSaveAction}
                variant="contained"
                color="primary"
                disabled={saveDisabled || verifyLoading}
                style={{ marginRight: 40 }}
            >
                <Trans>Save</Trans>
            </Button>
            {groupExist && (
                <Button onClick={handleVerifyAction} disabled={isActioning || verifyLoading}>
                    <Trans>Verify</Trans>
                </Button>
            )}
        </NgpRow>
    );
};

TestDetailBottomButtons.propTypes = {
    groupExist: PropTypes.bool.isRequired,
    formData: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    handleSubmitFunction: PropTypes.func.isRequired,
    submitTransform: PropTypes.func,
    saveDisabled: PropTypes.bool.isRequired,
    isActioning: PropTypes.bool.isRequired,
    onVerify: PropTypes.func,
    verifyLoading: PropTypes.bool.isRequired,
    bttStateRef: PropTypes.shape({ current: PropTypes.any }),
};

TestDetailBottomButtons.defaultProps = {
    formData: undefined,
    onVerify: undefined,
    submitTransform: undefined,
    bttStateRef: undefined,
};

export default TestDetailBottomButtons;
